/**
 * useSafeSave Hook
 * 
 * Prevents auto-save from overwriting good data when a component's data
 * hasn't fully loaded yet or when the user hasn't made any changes.
 * 
 * PROBLEM SOLVED:
 * When switching tabs, the auto-save mechanism calls silentSave() on the
 * current component. If the component's data hasn't loaded from the API yet
 * (or loaded incorrectly), silentSave() would save empty/default state,
 * overwriting the actual data on the server.
 * 
 * SOLUTION:
 * This hook tracks two flags:
 * 1. isDataLoaded - Set to true only after data successfully loads from API
 * 2. hasUserModified - Set to true when the user makes any change
 * 
 * silentSave is only allowed when BOTH conditions are true, OR when
 * isDataLoaded is true and forceAllowSave is called (for explicit saves).
 * 
 * ENHANCED SENSITIVITY:
 * - useFormChangeDetector() attaches DOM-level listeners (input, change)
 *   via event delegation on a container ref, catching text fields, checkboxes,
 *   dropdowns, radio buttons, and contentEditable changes automatically.
 * - markModified() can still be called manually for programmatic state changes.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * @param {string} componentName - Name of the component (for logging)
 * @returns {object} Safe save utilities
 */
export const useSafeSave = (componentName = 'Unknown') => {
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [hasUserModified, setHasUserModified] = useState(false);

    // Use refs for the latest values (avoids stale closure issues in silentSave)
    const isDataLoadedRef = useRef(false);
    const hasUserModifiedRef = useRef(false);

    /**
     * Call this when data has been successfully loaded from the API.
     * This "unlocks" the ability to save.
     */
    const markDataLoaded = useCallback(() => {
        isDataLoadedRef.current = true;
        setIsDataLoaded(true);
        console.log(`[SafeSave:${componentName}] ✅ Data loaded - save unlocked`);
    }, [componentName]);

    /**
     * Call this when data loading fails or when the component is resetting.
     * This "locks" saving to prevent overwriting good data.
     */
    const markDataNotLoaded = useCallback(() => {
        isDataLoadedRef.current = false;
        hasUserModifiedRef.current = false;
        setIsDataLoaded(false);
        setHasUserModified(false);
        console.log(`[SafeSave:${componentName}] 🔒 Data not loaded - save locked`);
    }, [componentName]);

    /**
     * Call this whenever the user modifies data (onChange, add, delete, etc.).
     * This marks that there are actual user changes worth saving.
     */
    const markModified = useCallback(() => {
        if (!hasUserModifiedRef.current) {
            hasUserModifiedRef.current = true;
            setHasUserModified(true);
            console.log(`[SafeSave:${componentName}] ✏️ User modification detected`);
        }
    }, [componentName]);

    /**
     * Reset the modified flag (call after a successful save).
     */
    const clearModified = useCallback(() => {
        hasUserModifiedRef.current = false;
        setHasUserModified(false);
    }, []);

    /**
     * Check if it's safe to perform an auto-save (silent save).
     * Auto-save should ONLY happen when:
     * 1. Data was successfully loaded from the API
     * 2. The user has actually modified something
     * 
     * @returns {boolean} Whether it's safe to auto-save
     */
    const canAutoSave = useCallback(() => {
        const loaded = isDataLoadedRef.current;
        const modified = hasUserModifiedRef.current;

        if (!loaded) {
            console.warn(`[SafeSave:${componentName}] ⛔ Auto-save BLOCKED - data not loaded yet`);
            return false;
        }

        if (!modified) {
            console.log(`[SafeSave:${componentName}] ⏭️ Auto-save SKIPPED - no user modifications`);
            return false;
        }

        console.log(`[SafeSave:${componentName}] ✅ Auto-save ALLOWED - data loaded & user modified`);
        return true;
    }, [componentName]);

    /**
     * Check if it's safe to perform an explicit save (user clicked Save button).
     * Explicit saves only require data to be loaded (user intent is clear).
     * 
     * @returns {boolean} Whether it's safe to explicitly save
     */
    const canExplicitSave = useCallback(() => {
        const loaded = isDataLoadedRef.current;

        if (!loaded) {
            console.warn(`[SafeSave:${componentName}] ⛔ Explicit save BLOCKED - data not loaded yet`);
            return false;
        }

        return true;
    }, [componentName]);

    /**
     * Wraps a handler to automatically call markModified() when invoked.
     * Usage: onChange={withModified(handleChange)}
     */
    const withModified = useCallback((handler) => {
        return (...args) => {
            markModified();
            if (handler) return handler(...args);
        };
    }, [markModified]);

    /**
     * Attaches DOM-level event listeners to a container ref to automatically
     * detect user interactions (typing, checking boxes, selecting dropdowns, etc.)
     * without needing manual markModified() calls on every handler.
     * 
     * Usage: const containerRef = useFormChangeDetector();
     *        return <div ref={containerRef}>...</div>
     * 
     * Detected events:
     * - input: text fields, textareas, contentEditable, number inputs
     * - change: checkboxes, radio buttons, select dropdowns, file inputs
     */
    const useFormChangeDetector = () => {
        const containerRef = useRef(null);

        useEffect(() => {
            const container = containerRef.current;
            if (!container) return;

            const handleInput = (e) => {
                // Only trigger if data is loaded (to skip initial renders/fills)
                if (!isDataLoadedRef.current) return;

                const target = e.target;
                const tagName = target.tagName?.toLowerCase();

                // Detect text input in input fields, textareas, and contentEditable
                if (
                    tagName === 'input' ||
                    tagName === 'textarea' ||
                    target.isContentEditable
                ) {
                    setTimeout(() => markModified(), 0);
                }
            };

            const handleChange = (e) => {
                if (!isDataLoadedRef.current) return;

                const target = e.target;
                const tagName = target.tagName?.toLowerCase();
                const inputType = target.type?.toLowerCase();

                // Detect checkbox, radio, select, and file changes
                if (
                    tagName === 'select' ||
                    inputType === 'checkbox' ||
                    inputType === 'radio' ||
                    inputType === 'file' ||
                    inputType === 'color' ||
                    inputType === 'range' ||
                    inputType === 'date' ||
                    inputType === 'datetime-local' ||
                    inputType === 'time'
                ) {
                    setTimeout(() => markModified(), 0);
                }
            };

            // Use capture phase for 'input' events to detect changes early.
            // Use bubble phase for 'change' events so React's own synthetic onChange
            // handler fires first (important for <select> elements — using capture
            // phase here caused a re-render before the selected value was committed,
            // requiring a double-click to select an option).
            container.addEventListener('input', handleInput, true);
            container.addEventListener('change', handleChange, false);

            return () => {
                container.removeEventListener('input', handleInput, true);
                container.removeEventListener('change', handleChange, false);
            };
        }, [markModified]);

        return containerRef;
    };

    return {
        // State (for UI display if needed)
        isDataLoaded,
        hasUserModified,

        // Actions
        markDataLoaded,
        markDataNotLoaded,
        markModified,
        clearModified,

        // Guards
        canAutoSave,
        canExplicitSave,

        // Helpers
        withModified,
        useFormChangeDetector,
    };
};

export default useSafeSave;
